
import React, { useState, useEffect, useRef } from 'react';
import { Role, ModuleID, AdminSettings } from '../types';
import { contactsApi } from '../services/contactsApi';

interface SidebarProps {
  currentRole: Role;
  onRoleChange?: (role: Role) => void;
  activeTab: ModuleID;
  setActiveTab: (tab: ModuleID) => void;
  settings: AdminSettings;
  premiumMode?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  canSwitchRoles?: boolean;
}

interface ModuleConfig {
  description: string;
  category: string;
  /** Tailwind gradient for the accent band */
  band: string;
  /** Icon container bg + text color (dark mode) */
  iconDark: string;
  /** Icon container bg + text color (light mode) */
  iconLight: string; 
  /** Accent text color (dark mode) */
  accentDark: string;
  /** Accent text color (light mode) */
  accentLight: string;
  /** Glow shadow for the icon bubble */
  glow: string;
}

interface NavItem {
  key: string;
  id: ModuleID;
  label: string;
  shortLabel?: string;
  icon: string;
  minRole?: Role[];
  route?: string;
  isActive?: (activeTab: ModuleID) => boolean;
}

const moduleConfig: Partial<Record<ModuleID, ModuleConfig>> = {
  DASHBOARD:   { description: 'Key metrics, activity feed & live operational overview', category: 'Operations',     band: 'from-indigo-500 via-blue-500 to-indigo-600',      iconDark: 'bg-indigo-500/20 text-indigo-300',   iconLight: 'bg-indigo-100 text-indigo-600',   accentDark: 'text-indigo-400',  accentLight: 'text-indigo-500',  glow: 'shadow-indigo-500/40' },
  OPERATIONS_TV: { description: 'Premium wallboard mode for live fleet command visibility', category: 'Executive Ops', band: 'from-amber-300 via-yellow-400 to-emerald-400', iconDark: 'bg-amber-400/20 text-amber-200', iconLight: 'bg-amber-50 text-amber-700', accentDark: 'text-amber-300', accentLight: 'text-amber-700', glow: 'shadow-amber-400/40' },
  STRATEGY:    { description: 'Executive analytics, strategic KPIs & performance trends', category: 'Analytics',     band: 'from-emerald-500 via-teal-500 to-emerald-600',    iconDark: 'bg-emerald-500/20 text-emerald-300', iconLight: 'bg-emerald-100 text-emerald-600', accentDark: 'text-emerald-400', accentLight: 'text-emerald-600', glow: 'shadow-emerald-500/40' },
  QUOTER:      { description: 'Charter pricing, quote builder & approval workflow',       category: 'Sales',          band: 'from-amber-400 via-yellow-500 to-amber-500',      iconDark: 'bg-amber-500/20 text-amber-300',     iconLight: 'bg-amber-100 text-amber-600',     accentDark: 'text-amber-400',   accentLight: 'text-amber-600',   glow: 'shadow-amber-500/40' },
  INBOX:       { description: 'Inbound requests, emails & unified communications',        category: 'Communications', band: 'from-sky-500 via-blue-500 to-sky-600',            iconDark: 'bg-sky-500/20 text-sky-300',         iconLight: 'bg-sky-100 text-sky-600',         accentDark: 'text-sky-400',     accentLight: 'text-sky-600',     glow: 'shadow-sky-500/40' },
  SCHEDULED:   { description: 'Scheduled flights, rotations & service management',        category: 'Flight Ops',     band: 'from-violet-500 via-purple-500 to-violet-600',    iconDark: 'bg-violet-500/20 text-violet-300',   iconLight: 'bg-violet-100 text-violet-600',   accentDark: 'text-violet-400',  accentLight: 'text-violet-600',  glow: 'shadow-violet-500/40' },
  PLANNER:     { description: 'Mission planning matrix & resource allocation board',       category: 'Planning',       band: 'from-orange-500 via-amber-500 to-orange-600',     iconDark: 'bg-orange-500/20 text-orange-300',   iconLight: 'bg-orange-100 text-orange-600',   accentDark: 'text-orange-400',  accentLight: 'text-orange-600',  glow: 'shadow-orange-500/40' },
  PILOTS:      { description: 'Pilot profiles, certifications & duty-time records',       category: 'Personnel',      band: 'from-blue-500 via-indigo-500 to-blue-600',        iconDark: 'bg-blue-500/20 text-blue-300',       iconLight: 'bg-blue-100 text-blue-600',       accentDark: 'text-blue-400',    accentLight: 'text-blue-600',    glow: 'shadow-blue-500/40' },
  FRMS:        { description: 'Fatigue risk scores, limits & duty-time compliance',        category: 'Safety',         band: 'from-red-500 via-rose-500 to-red-600',            iconDark: 'bg-red-500/20 text-red-300',         iconLight: 'bg-red-100 text-red-600',         accentDark: 'text-red-400',     accentLight: 'text-red-600',     glow: 'shadow-red-500/40' },
  CUSTOMERS:   { description: 'Client relationships, trip history & CRM tools',           category: 'CRM',            band: 'from-teal-500 via-cyan-500 to-teal-600',          iconDark: 'bg-teal-500/20 text-teal-300',       iconLight: 'bg-teal-100 text-teal-600',       accentDark: 'text-teal-400',    accentLight: 'text-teal-600',    glow: 'shadow-teal-500/40' },
  CONTACTS:    { description: 'Contact directory, follow-up tasks & activity log',         category: 'Directory',      band: 'from-purple-500 via-violet-500 to-purple-600',    iconDark: 'bg-purple-500/20 text-purple-300',   iconLight: 'bg-purple-100 text-purple-600',   accentDark: 'text-purple-400',  accentLight: 'text-purple-600',  glow: 'shadow-purple-500/40' },
  FINANCE:     { description: 'Revenue tracking, invoices & financial reporting',          category: 'Finance',        band: 'from-green-500 via-emerald-500 to-green-600',     iconDark: 'bg-green-500/20 text-green-300',     iconLight: 'bg-green-100 text-green-600',     accentDark: 'text-green-400',   accentLight: 'text-green-600',   glow: 'shadow-green-500/40' },
  ADMIN:       { description: 'System settings, user permissions & module registry',       category: 'System',         band: 'from-slate-500 via-gray-500 to-slate-600',        iconDark: 'bg-slate-500/20 text-slate-300',     iconLight: 'bg-slate-100 text-slate-600',     accentDark: 'text-slate-400',   accentLight: 'text-slate-600',   glow: 'shadow-slate-500/40' },
  ADMIN_TOOLS: { description: 'Administrative tools, data management & control panel',    category: 'Admin',          band: 'from-zinc-500 via-slate-500 to-zinc-600',         iconDark: 'bg-zinc-500/20 text-zinc-300',       iconLight: 'bg-zinc-100 text-zinc-600',       accentDark: 'text-zinc-400',    accentLight: 'text-zinc-600',    glow: 'shadow-zinc-500/40' },
  OUTBOUND:    { description: 'Outbound campaigns, templates & communications hub',        category: 'Marketing',      band: 'from-pink-500 via-rose-500 to-pink-600',          iconDark: 'bg-pink-500/20 text-pink-300',       iconLight: 'bg-pink-100 text-pink-600',       accentDark: 'text-pink-400',    accentLight: 'text-pink-600',    glow: 'shadow-pink-500/40' },
  CEO_HUB:     { description: 'CEO dashboard, SOPs, task board & announcements',          category: 'Executive',      band: 'from-amber-300 via-yellow-400 to-amber-400',      iconDark: 'bg-amber-400/20 text-amber-200',     iconLight: 'bg-amber-50 text-amber-600',      accentDark: 'text-amber-300',   accentLight: 'text-amber-600',   glow: 'shadow-amber-400/50' },
  COMMS:       { description: 'Unified comms metrics, channels & performance monitoring',  category: 'Communications', band: 'from-cyan-500 via-sky-500 to-cyan-600',           iconDark: 'bg-cyan-500/20 text-cyan-300',       iconLight: 'bg-cyan-100 text-cyan-600',       accentDark: 'text-cyan-400',    accentLight: 'text-cyan-600',    glow: 'shadow-cyan-500/40' },
  AUDIT:       { description: 'Full audit trail, change ledger & compliance records',      category: 'Compliance',     band: 'from-rose-500 via-red-500 to-rose-600',           iconDark: 'bg-rose-500/20 text-rose-300',       iconLight: 'bg-rose-100 text-rose-600',       accentDark: 'text-rose-400',    accentLight: 'text-rose-600',    glow: 'shadow-rose-500/40' },
};

const HamburgerIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="3" y1="5"    x2="21" y2="5" />
    <line x1="3" y1="9.67" x2="21" y2="9.67" />
    <line x1="3" y1="14.33" x2="21" y2="14.33" />
    <line x1="3" y1="19"   x2="21" y2="19" />
  </svg>
);

const Sidebar: React.FC<SidebarProps> = ({
  currentRole, onRoleChange, activeTab, setActiveTab,
  settings, premiumMode = false, collapsed = false,
  onToggleCollapsed, canSwitchRoles = false,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [overdueFollowUpCount, setOverdueFollowUpCount] = useState(0);
  const [hoveredItem, setHoveredItem] = useState<{
    id: ModuleID; label: string; icon: string; description: string;
    category: string; cfg: ModuleConfig | undefined; y: number; active: boolean;
  } | null>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    contactsApi.getMyFollowUps()
      .then(list => setOverdueFollowUpCount(list.filter(f => new Date(f.dueAt) < new Date()).length))
      .catch(() => {});
  }, []);

  const roleInitials: Record<string, string> = {
    CEO: 'CE', ADMIN: 'SA', DFO: 'DF',
    FLIGHT_PLANNER: 'FP', RESERVATIONS: 'RS', ACCOUNTS: 'AC',
  };
  const initials = roleInitials[currentRole as string] ?? currentRole.slice(0, 2);

  const getFormattedTime = (tz: string, color: string = premiumMode ? 'text-white' : 'text-slate-900') => {
    const timeStr = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, timeZone: tz,
    }).format(currentTime);
    return <span className={`${color} font-mono tracking-tighter`}>{timeStr}</span>;
  };

  const allNavItems: NavItem[] = [
    { key: 'DASHBOARD', label: 'Command Center', id: 'DASHBOARD', shortLabel: 'Cmd', icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z' },
    { key: 'OPERATIONS_TV', label: 'Operations TV', id: 'OPERATIONS_TV', shortLabel: 'Live Board', icon: 'M3 5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-5l1.5 3h1.5a1 1 0 110 2H7a1 1 0 010-2h1.5L10 17H5a2 2 0 01-2-2V5zm2 0v10h14V5H5z' },
    { key: 'STRATEGY', label: 'Executive Insights', id: 'STRATEGY', shortLabel: 'Ins', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6', minRole: ['CEO','ADMIN'] },
    { key: 'QUOTER', label: 'Charter Dashboard', id: 'QUOTER', shortLabel: 'Char', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
    { key: 'INBOX', label: 'Inbound Hub', id: 'INBOX', shortLabel: 'Inbox', icon: 'M20 13V7a2 2 0 00-2-2h-3V3H9v2H6a2 2 0 00-2 2v6m16 0v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6m16 0h-3m-10 0H4m3 0a3 3 0 006 0m-6 0a3 3 0 01-6 0m12 0a3 3 0 006 0' },
    { key: 'RESERVATION', label: 'Reservation', id: 'SCHEDULED', shortLabel: 'Res', route: '#/reservations/aerocrs', isActive: (activeTab) => activeTab === 'SCHEDULED' && isReservationRoute(), icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z' },
    { key: 'SCHEDULED', label: 'Scheduled Service', id: 'SCHEDULED', shortLabel: 'Sch', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z' },
    { key: 'PLANNER', label: 'Planner Matrix', id: 'PLANNER', shortLabel: 'Plan', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
    { key: 'PILOTS', label: 'Pilot Registry', id: 'PILOTS', shortLabel: 'Pilots', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { key: 'FRMS', label: 'FRMS Authority', id: 'FRMS', shortLabel: 'FRMS', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
    { key: 'CUSTOMERS', label: 'Client Relations', id: 'CUSTOMERS', shortLabel: 'Clients', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { key: 'CONTACTS', label: 'Contacts Hub', id: 'CONTACTS', shortLabel: 'Contacts', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { key: 'FINANCE', label: 'Finance Central', id: 'FINANCE', shortLabel: 'Finance', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2' },
    { key: 'ADMIN', label: 'System Registry', id: 'ADMIN', shortLabel: 'System', icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4', minRole: ['ADMIN'] },
    { key: 'ADMIN_TOOLS', label: 'Admin Center', id: 'ADMIN_TOOLS', shortLabel: 'Admin', icon: 'M12 8c1.657 0 3-1.567 3-3.5S13.657 1 12 1 9 2.567 9 4.5 10.343 8 12 8zm0 2c-3.314 0-6 2.239-6 5v4h12v-4c0-2.761-2.686-5-6-5z', minRole: ['ADMIN'] },
    { key: 'OUTBOUND', label: 'Outbound Hub', id: 'OUTBOUND', shortLabel: 'Outbound', icon: 'M3 12l9-9 9 9M5 10v10a2 2 0 002 2h10a2 2 0 002-2V10', minRole: ['ADMIN'] },
    { key: 'CEO_HUB', label: 'CEO Hub', id: 'CEO_HUB', shortLabel: 'CEO Hub', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', minRole: ['CEO','ADMIN'] },
    { key: 'COMMS', label: 'Comms Dashboard', id: 'COMMS', shortLabel: 'Comms', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', minRole: ['ADMIN','CEO'] },
    { key: 'AUDIT', label: 'Audit Ledger', id: 'AUDIT', shortLabel: 'Audit', icon: 'M9 17v2h6v-2m-6-6h6m-7 10h8a2 2 0 002-2V7a2 2 0 00-2-2h-3l-1-2h-4l-1 2H6a2 2 0 00-2 2v12a2 2 0 002 2z', minRole: ['ADMIN','CEO'] },
  ];

  // Temporarily hidden from the main sidebar without removing the underlying modules.
  const hiddenNavKeys = new Set([
    'DASHBOARD',
    'OPERATIONS_TV',
    'QUOTER',
    'RESERVATION',
    'SCHEDULED',
    'PLANNER',
    'PILOTS',
    'FRMS',
  ]);

  const rolePerms = settings.permissions[currentRole];
  const navItems = allNavItems.filter(item => {
    if (hiddenNavKeys.has(item.key)) return false;
    if (item.id === 'OPERATIONS_TV') return true;
    if (item.id === 'INBOX')    return !!(rolePerms as any)?.INBOX?.view;
    if (item.id === 'CONTACTS') return !!(rolePerms as any)?.CONTACTS?.view;
    if (item.id === 'CEO_HUB')  return item.minRole ? item.minRole.includes(currentRole) : false;
    return rolePerms && (rolePerms as any)[item.id]?.view;
  });

  const handleTabClick = (item: NavItem) => {
    setActiveTab(item.id);
    window.location.hash = item.route || `#/${item.id.toLowerCase()}`;
  };

  const handleNavMouseEnter = (e: React.MouseEvent<HTMLButtonElement>, item: typeof navItems[0]) => {
    if (!collapsed) return;
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const cfg = moduleConfig[item.id];
    setHoveredItem({
      id: item.id,
      label: item.label,
      icon: item.icon,
      description: cfg?.description ?? '',
      category: cfg?.category ?? '',
      cfg,
      y: rect.top + rect.height / 2,
      active: item.isActive ? item.isActive(activeTab) : activeTab === item.id,
    });
  };

  const handleNavMouseLeave = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setHoveredItem(null), 120);
  };

  // Collapsed sidebar width tuned to Gmail-like compact rail size.
  const COLLAPSED_WIDTH = 62;
  const EXPANDED_WIDTH = 264;

  return (
    <>
      <aside
        className={`relative flex flex-col h-full shrink-0 border-r z-10 shadow-xl transition-[width] duration-300 ${
          premiumMode
            ? 'bg-[linear-gradient(180deg,#0a1226_0%,#091933_45%,#081325_100%)] text-white border-amber-300/25 shadow-[0_20px_60px_rgba(2,6,23,0.75)]'
            : 'bg-white text-slate-800 border-slate-200'
        }`}
        style={{ width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH }}
      >
        {/* ── Hamburger toggle ── */}
        {onToggleCollapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={`absolute z-20 inline-flex items-center justify-center rounded-xl border transition-all duration-200 ${
              collapsed ? 'right-2 top-2 h-8 w-8' : 'right-2 top-3 h-8 w-8'
            } ${
              premiumMode
                ? 'border-amber-300/30 bg-[#0b1a35]/80 text-amber-300 hover:bg-amber-300/15 hover:border-amber-300/55 hover:shadow-[0_0_12px_rgba(252,211,77,0.25)]'
                : 'border-slate-200 bg-white/90 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            }`}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <HamburgerIcon className="w-4 h-4" />
          </button>
        ) : null}

        {/* ── Logo / brand header ── */}
        <div
          className={`${collapsed ? 'px-2 pt-12 pb-3 justify-center overflow-hidden' : 'px-5 pt-14 pb-5'} flex items-center gap-3.5 border-b cursor-pointer select-none ${
            premiumMode ? 'border-amber-300/20 bg-[radial-gradient(circle_at_20%_0%,rgba(245,158,11,0.16),transparent_45%)]' : 'border-slate-200'
          }`}
          onClick={() => handleTabClick(allNavItems[0])}
          title="Back to Dashboard"
        >
          {/* Logo mark */}
          <div
            className={`rounded-2xl flex items-center justify-center overflow-hidden relative shrink-0 transition-all duration-300 ${
              collapsed ? 'w-9 h-9' : 'w-11 h-11 hover:scale-110'
            }`}
            style={{
              background: 'linear-gradient(145deg, #2a1e06 0%, #1a1206 100%)',
              boxShadow: collapsed
                ? '0 2px 8px rgba(0,0,0,0.5)'
                : '0 6px 20px rgba(0,0,0,0.6), 0 0 24px rgba(212,175,55,0.2)',
            }}
          >
            <img
              src="/meos/images/me-logo-icon.png"
              alt="ME-OS Logo"
              className="w-full h-full object-contain p-0.5"
              style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' }}
              onError={(e) => {
                if (e.currentTarget.style.display !== 'none') {
                  e.currentTarget.style.display = 'none';
                  const fb = e.currentTarget.parentElement?.querySelector('.logo-fallback') as HTMLElement;
                  if (fb) fb.style.display = 'flex';
                }
              }}
            />
            <div
              className="logo-fallback w-full h-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center text-amber-200 font-black text-xs"
              style={{ display: 'none' }}
            >
              ME
            </div>
          </div>

          {!collapsed ? (
            <div className="flex flex-col">
              <h2
                className="text-xl font-black leading-none tracking-tighter uppercase"
                style={{
                  backgroundImage: 'linear-gradient(135deg, #f4d03f 0%, #d4af37 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                ME-OS
              </h2>
              <span className={`text-[10px] font-black uppercase tracking-[0.25em] mt-1 ${premiumMode ? 'text-amber-400' : 'text-slate-500'}`}>
                Enterprise
              </span>
            </div>
          ) : null}
        </div>

        {/* ── Clock ── */}
        {!collapsed && (
        <div className={`${collapsed ? 'px-3 py-4' : 'px-10 py-6 mb-4'} border-y ${premiumMode ? 'border-amber-400/10' : 'border-slate-200'}`}>
          <div className="space-y-1">
            {!collapsed ? (
              <p className={`text-[9px] font-black uppercase tracking-[0.2em] ${premiumMode ? 'text-amber-400/60' : 'text-slate-500'}`}>
                Operational Epoch (EAT)
              </p>
            ) : null}
            <p
              className={`${collapsed ? 'text-sm text-center' : 'text-2xl'} font-black`}
              style={{
                backgroundImage: 'linear-gradient(135deg, #f4d03f 0%, #d4af37 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {getFormattedTime('Africa/Nairobi', premiumMode ? 'text-white' : 'text-slate-900')}
            </p>
            {!collapsed ? (
              <div className="flex items-center gap-2 pt-1.5 opacity-60 hover:opacity-100 transition-all duration-300">
                <span className={`text-[10px] font-bold uppercase tracking-widest ${premiumMode ? 'text-amber-400/60' : 'text-slate-400'}`}>
                  {getFormattedTime('UTC', premiumMode ? 'text-amber-400/60' : 'text-slate-500')}{' '}
                  <span className="ml-1 text-[8px] font-black">GMT</span>
                </span>
              </div>
            ) : null}
          </div>
        </div>
        )}

        {/* ── Navigation ── */}
        <nav className={`flex-1 ${collapsed ? 'px-1.5 py-2 overflow-y-auto overflow-x-visible' : 'px-3 py-3 overflow-y-auto'} space-y-0.5 custom-scrollbar`}>
          {(() => {
            const GROUP_STARTS = new Set(['QUOTER', 'PILOTS', 'ADMIN', 'CEO_HUB']);
            return navItems.map((item, index) => {
              const isActive = item.isActive ? item.isActive(activeTab) : activeTab === item.id;
              const showDivider = !collapsed && index > 0 && GROUP_STARTS.has(item.key);
              return (
                <React.Fragment key={item.key}>
                  {showDivider && (
                    <div className={`mx-2 my-2 border-t ${premiumMode ? 'border-amber-300/10' : 'border-slate-100'}`} />
                  )}
                  <button
                    onClick={() => handleTabClick(item)}
                    onMouseEnter={(e) => handleNavMouseEnter(e, item)}
                    onMouseLeave={handleNavMouseLeave}
                    className={`w-full flex items-center rounded-xl group relative cursor-pointer ${
                      collapsed
                        ? 'flex-col justify-center gap-1 px-1 py-2.5 min-h-[64px] text-[13px] font-medium transition-colors duration-200 overflow-hidden'
                        : 'flex-row gap-3 px-3 py-2.5 text-[13px] transition-all duration-200'
                    } ${
                      isActive
                        ? collapsed
                          ? premiumMode
                            ? 'bg-amber-300/20 text-amber-100'
                            : 'bg-slate-900/8 text-slate-900'
                          : premiumMode
                            ? 'bg-[linear-gradient(135deg,#fcd34d_0%,#f59e0b_100%)] text-[#3a2600] shadow-[0_14px_30px_rgba(245,158,11,0.45)]'
                            : 'bg-slate-900 text-white'
                        : premiumMode
                          ? 'text-amber-100/70 hover:bg-amber-300/10 hover:text-amber-100 hover:shadow-[inset_0_0_0_1px_rgba(252,211,77,0.22)]'
                          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                    }`}
                    style={
                      isActive && premiumMode && !collapsed
                        ? { boxShadow: '0 14px 34px rgba(245,158,11,0.35), inset -1px -1px 4px rgba(120,53,15,0.25), inset 1px 1px 4px rgba(255,255,255,0.28)' }
                        : {}
                    }
                  >
                    {/* Amber left accent bar for active light mode */}
                    {isActive && !premiumMode && !collapsed && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] bg-amber-400 rounded-r-full" />
                    )}

                    {/* Shimmer sweep on hover (expanded only) */}
                    {!collapsed && !isActive && (
                      <span
                        className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{
                          background: premiumMode
                            ? 'linear-gradient(105deg,transparent 40%,rgba(252,211,77,0.07) 50%,transparent 60%)'
                            : 'linear-gradient(105deg,transparent 40%,rgba(15,23,42,0.03) 50%,transparent 60%)',
                        }}
                      />
                    )}

                    {/* Icon */}
                    <svg
                      className={`shrink-0 transition-all duration-200 w-[18px] h-[18px] ${
                        collapsed
                          ? ''
                          : isActive
                            ? ''
                            : 'group-hover:opacity-90'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{
                        filter: isActive && premiumMode && !collapsed ? 'drop-shadow(0 2px 4px rgba(212,175,55,0.6))' : undefined,
                      }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                    </svg>

                    {/* Expanded: label */}
                    {!collapsed ? (
                      <span className={`tracking-[-0.01em] flex-1 flex items-center justify-between ${isActive ? 'font-semibold' : 'font-medium'}`}>
                        {item.label}
                        {item.id === 'CONTACTS' && overdueFollowUpCount > 0 ? (
                          <span className="ml-2 text-[10px] font-black bg-red-500 text-white rounded-full px-1.5 py-0.5 leading-none">
                            {overdueFollowUpCount}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <>
                        <span
                          className={`text-[9px] font-semibold leading-[1.2] tracking-[0.01em] text-center w-full px-0.5 line-clamp-2 ${
                            isActive
                              ? premiumMode ? 'text-amber-100' : 'text-slate-900'
                              : premiumMode
                                ? 'text-amber-100/65 group-hover:text-amber-100/85'
                                : 'text-slate-500 group-hover:text-slate-700'
                          }`}
                        >
                          {item.shortLabel ?? item.label}
                        </span>
                        {item.id === 'CONTACTS' && overdueFollowUpCount > 0 ? (
                          <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full shadow-md shadow-red-500/50" />
                        ) : null}
                      </>
                    )}
                  </button>
                </React.Fragment>
              );
            });
          })()}
        </nav>

        {/* ── Role / Identity footer ── */}
        <div className={`${collapsed ? 'p-2.5' : 'px-3 py-3'} border-t ${premiumMode ? 'border-amber-400/10' : 'border-slate-100'}`}>
          {!collapsed ? (
            <div
              className={`rounded-xl p-3 flex items-center gap-3 ${
                premiumMode
                  ? 'bg-[linear-gradient(145deg,rgba(245,158,11,0.14),rgba(250,204,21,0.04))] border border-amber-300/25'
                  : 'bg-slate-50 border border-slate-100'
              }`}
            >
              {/* Avatar circle */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[10px] font-black tracking-wide ${
                  premiumMode
                    ? 'bg-gradient-to-br from-amber-500/40 to-amber-400/20 text-amber-200 ring-1 ring-amber-400/40'
                    : 'bg-gradient-to-br from-slate-700 to-slate-900 text-white ring-2 ring-slate-100'
                }`}
              >
                {initials}
              </div>

              {/* Role info */}
              <div className="flex-1 min-w-0">
                <p className={`text-[9px] font-black uppercase tracking-[0.18em] leading-none mb-0.5 ${premiumMode ? 'text-amber-400/50' : 'text-slate-400'}`}>
                  {canSwitchRoles ? 'Identity' : 'Role'}
                </p>
                {canSwitchRoles && onRoleChange ? (
                  <select
                    value={currentRole}
                    onChange={(e) => onRoleChange(e.target.value as Role)}
                    className={`w-full text-[12px] font-semibold outline-none cursor-pointer bg-transparent truncate ${
                      premiumMode ? 'text-amber-200' : 'text-slate-800'
                    }`}
                  >
                    <option value="CEO">Chief Executive</option>
                    <option value="ADMIN">System Architect</option>
                    <option value="DFO">Dir. Flight Ops</option>
                    <option value="FLIGHT_PLANNER">Flight Planner</option>
                    <option value="RESERVATIONS">Reservations</option>
                    <option value="ACCOUNTS">Accounts</option>
                  </select>
                ) : (
                  <p className={`text-[12px] font-semibold truncate ${premiumMode ? 'text-amber-200' : 'text-slate-800'}`}>
                    {currentRole.replace('_', ' ')}
                  </p>
                )}
              </div>

              {/* Chevron if switchable */}
              {canSwitchRoles && (
                <svg className={`w-3.5 h-3.5 shrink-0 ${premiumMode ? 'text-amber-400/40' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
          ) : (
            /* Collapsed: avatar only */
            <div className="flex justify-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black tracking-wide ${
                  premiumMode
                    ? 'bg-gradient-to-br from-amber-500/40 to-amber-400/20 text-amber-200 ring-1 ring-amber-400/40'
                    : 'bg-gradient-to-br from-slate-700 to-slate-900 text-white ring-2 ring-slate-100'
                }`}
              >
                {initials}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ── Collapsed hover preview panel (fixed, floats right of sidebar) ── */}
      {collapsed && hoveredItem && (() => {
        // Clamp so card doesn't go off-screen
        const topY = Math.min(Math.max(hoveredItem.y, 80), window.innerHeight - 200);

        return (
          <div
            className="pointer-events-none fixed z-[9999]"
            style={{ left: COLLAPSED_WIDTH + 10, top: topY, transform: 'translateY(-50%)' }}
            onMouseEnter={() => { if (hoverTimeout.current) clearTimeout(hoverTimeout.current); }}
            onMouseLeave={handleNavMouseLeave}
          >
            {/* Arrow connector */}
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[7px]"
              style={{
                width: 0, height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: premiumMode ? '8px solid rgba(255,255,255,0.08)' : '8px solid #e2e8f0',
              }}
            />

            <div
              className={`w-56 rounded-2xl overflow-hidden animate-fadeSlideIn pointer-events-auto ${
                premiumMode
                  ? 'bg-[linear-gradient(160deg,#0e2040_0%,#091530_100%)] border border-white/8 shadow-[0_16px_48px_rgba(2,6,23,0.8),0_0_0_1px_rgba(255,255,255,0.05)]'
                  : 'bg-white border border-slate-200 shadow-[0_8px_32px_rgba(15,23,42,0.10),0_1px_4px_rgba(15,23,42,0.06)]'
              }`}
            >
              {/* Slim amber top accent — consistent, not per-module color */}
              <div className={`h-[2px] ${premiumMode ? 'bg-gradient-to-r from-amber-400/60 to-amber-300/20' : 'bg-gradient-to-r from-amber-400 to-amber-200'}`} />

              {/* Icon + label */}
              <div className="px-4 pt-3.5 pb-3 flex items-center gap-3">
                {/* Icon box — neutral, not colorful */}
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    premiumMode ? 'bg-white/8 text-amber-200' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 17, height: 17 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={hoveredItem.icon} />
                  </svg>
                </div>

                <div className="flex-1 min-w-0">
                  {/* Category label */}
                  {hoveredItem.category ? (
                    <p className={`text-[9px] font-bold uppercase tracking-[0.16em] leading-none mb-1 ${
                      premiumMode ? 'text-white/30' : 'text-slate-400'
                    }`}>
                      {hoveredItem.category}
                    </p>
                  ) : null}
                  {/* Module name */}
                  <p className={`text-[13px] font-semibold leading-tight tracking-[-0.01em] ${
                    premiumMode ? 'text-white' : 'text-slate-900'
                  }`}>
                    {hoveredItem.label}
                  </p>
                </div>

                {/* Active dot */}
                {hoveredItem.active && (
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
                )}
              </div>

              {/* Description */}
              {hoveredItem.description ? (
                <>
                  <div className={`mx-4 h-px ${premiumMode ? 'bg-white/6' : 'bg-slate-100'}`} />
                  <div className="px-4 py-2.5">
                    <p className={`text-[11px] leading-[1.6] ${
                      premiumMode ? 'text-white/40' : 'text-slate-500'
                    }`}>
                      {hoveredItem.description}
                    </p>
                  </div>
                </>
              ) : null}

              {/* CTA — matches sidebar active state style */}
              <div className="px-4 pb-3.5">
                <button
                  className={`w-full rounded-xl px-3 py-2 flex items-center justify-between cursor-pointer transition-all duration-150 active:scale-[0.98] ${
                    premiumMode
                      ? 'bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/20'
                      : 'bg-slate-900 hover:bg-slate-700'
                  }`}
                  onClick={() => {
                    const matched = allNavItems.find((item) => item.id === hoveredItem.id && item.label === hoveredItem.label) || allNavItems.find((item) => item.id === hoveredItem.id);
                    if (matched) handleTabClick(matched);
                  }}
                >
                  <span className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${premiumMode ? 'text-amber-200' : 'text-white'}`}>
                    Open
                  </span>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    className={`w-3.5 h-3.5 ${premiumMode ? 'text-amber-300' : 'text-white'}`}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-50%) translateX(-6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(-50%) translateX(0)    scale(1);    }
        }
        .animate-fadeSlideIn { animation: fadeSlideIn 0.18s cubic-bezier(0.16,1,0.3,1) both; }
      `}</style>
    </>
  );
};

export default Sidebar;
  const isReservationRoute = () => {
    const hash = window.location.hash.toLowerCase();
    const path = window.location.pathname.toLowerCase();
    return hash.startsWith('#/reservations/aerocrs') || /\/reservations\/aerocrs\/?$/.test(path);
  };
